import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Appointment, Job } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { useSchedule, useUnassignedJobs } from '../lib/hooks.js';
import { Badge, Button, Card, Dialog, EmptyState, Field, Input, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label, statusTone } from '../lib/format.js';

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7:00 – 19:00
const HOUR_PX = 56;

function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function addDaysISO(date: string, d: number): string {
  const dt = new Date(`${date}T00:00:00.000Z`); dt.setUTCDate(dt.getUTCDate() + d); return dt.toISOString().slice(0, 10);
}
function fmtDay(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}
function localHM(iso: string): string { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }

export function Schedule() {
  const [date, setDate] = useState(todayISO());
  const { data, isLoading } = useSchedule(date);
  const unassigned = useUnassignedJobs();
  const [scheduleJob, setScheduleJob] = useState<Job | null>(null);
  const [editAppt, setEditAppt] = useState<Appointment | null>(null);
  const appts = (data?.items ?? []).filter((a) => a.status !== 'canceled');

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Schedule</div><div className="page-sub">{fmtDay(date)}</div></div>
        <div className="row gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDate(addDaysISO(date, -1))}>‹ Prev</Button>
          <Button variant="secondary" size="sm" onClick={() => setDate(todayISO())}>Today</Button>
          <Button variant="secondary" size="sm" onClick={() => setDate(addDaysISO(date, 1))}>Next ›</Button>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>

      <div className="sched">
        <Card>
          {isLoading ? (
            <div className="card-pad stack gap-3">{HOURS.slice(0, 6).map((h) => <Skeleton key={h} h={40} />)}</div>
          ) : (
            <div className="timeline">
              {HOURS.map((h) => (
                <div className="tl-row" key={h}>
                  <div className="tl-hour">{h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'a' : 'p'}</div>
                  <div className="tl-lane">
                    {h === HOURS[0] && appts.map((a) => <ApptBlock key={a.id} appt={a} onClick={() => setEditAppt(a)} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="card-head"><span className="card-title">Needs scheduling <span className="subtle">· {unassigned.data?.items.length ?? 0}</span></span></div>
          <div style={{ padding: '0 var(--sp-5)' }}>
            {(unassigned.data?.items.length ?? 0) === 0 ? (
              <div style={{ padding: 'var(--sp-6) 0' }}><span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Everything's scheduled 🎉</span></div>
            ) : unassigned.data!.items.map((j) => (
              <div key={j.id} className="queue-item">
                <div className="stack"><strong style={{ fontSize: 'var(--text-sm)' }}>{j.title}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{j.number}</span></div>
                <div className="row gap-2"><Badge tone={statusTone(j.priority)}>{label(j.priority)}</Badge><Button size="sm" onClick={() => setScheduleJob(j)}>Schedule</Button></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {scheduleJob && (
        <ScheduleDialog job={scheduleJob} defaultDate={date} onClose={() => setScheduleJob(null)} />
      )}
      {editAppt && <RescheduleDialog appt={editAppt} onClose={() => setEditAppt(null)} />}
    </>
  );
}

function RescheduleDialog({ appt, onClose }: { appt: Appointment; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const startD = new Date(appt.start);
  const [date, setDate] = useState(appt.start.slice(0, 10));
  const [time, setTime] = useState(`${String(startD.getHours()).padStart(2, '0')}:${String(startD.getMinutes()).padStart(2, '0')}`);
  const [durationH, setDurationH] = useState(String(Math.max(0.5, (new Date(appt.end).getTime() - startD.getTime()) / 3.6e6)));
  const [busy, setBusy] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['schedule'] }); qc.invalidateQueries({ queryKey: ['dispatch'] }); };

  const save = async () => {
    setBusy(true);
    const start = new Date(`${date}T${time}:00`);
    const end = new Date(start.getTime() + (parseFloat(durationH || '1') || 1) * 3.6e6);
    try { await api.patch(`/appointments/${appt.id}`, { start: start.toISOString(), end: end.toISOString() }); refresh(); toast.push('Appointment rescheduled', 'success'); onClose(); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
    finally { setBusy(false); }
  };
  const cancelAppt = async () => {
    setBusy(true);
    try { await api.post(`/appointments/${appt.id}/cancel`); refresh(); toast.push('Appointment canceled', 'success'); onClose(); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog title={`Reschedule · ${appt.title}`} onClose={onClose}
      footer={<><Button variant="danger" onClick={cancelAppt} loading={busy}>Cancel appt</Button><div className="grow" /><Button variant="secondary" onClick={onClose}>Close</Button><Button loading={busy} onClick={save}>Save</Button></>}>
      <div className="row gap-3">
        <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Start"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
        <Field label="Hours"><Input value={durationH} onChange={(e) => setDurationH(e.target.value)} style={{ width: 70 }} inputMode="decimal" /></Field>
      </div>
    </Dialog>
  );
}

function ApptBlock({ appt, onClick }: { appt: Appointment; onClick: () => void }) {
  const start = new Date(appt.start);
  const end = new Date(appt.end);
  const startH = start.getHours() + start.getMinutes() / 60;
  const durH = Math.max(0.5, (end.getTime() - start.getTime()) / 3.6e6);
  const lastHour = HOURS[HOURS.length - 1]! + 1;
  if (startH >= lastHour || startH + durH <= HOURS[0]!) return null; // entirely outside the window
  const rawTop = (startH - HOURS[0]!) * HOUR_PX;
  const top = Math.max(0, rawTop);
  const maxHeight = (lastHour - HOURS[0]!) * HOUR_PX - top - 4;
  const height = Math.min(durH * HOUR_PX - 4, maxHeight);
  return (
    <div className={`tl-appt appt-${statusTone(appt.status) === 'success' ? 'success' : 'info'}`} style={{ top: Math.max(0, top), height }} onClick={onClick} title={appt.title}>
      <strong>{appt.title}</strong>
      {localHM(appt.start)}–{localHM(appt.end)}
    </div>
  );
}

function ScheduleDialog({ job, defaultDate, onClose }: { job: Job; defaultDate: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('09:00');
  const [durationH, setDurationH] = useState('2');
  const [tech, setTech] = useState('usr_tech1');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<string[]>([]);

  const submit = async () => {
    setSaving(true); setConflicts([]);
    const start = new Date(`${date}T${time}:00`);
    const end = new Date(start.getTime() + (parseFloat(durationH || '1') || 1) * 3.6e6);
    try {
      const res = await api.post<{ conflicts: { title: string; start: string }[] }>(`/customers/${job.customerId}/jobs/${job.id}/appointments`, {
        start: start.toISOString(), end: end.toISOString(), assignedUserIds: tech ? [tech] : [],
      });
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['jobs', 'needs_scheduling'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (res.conflicts?.length) {
        toast.push(`Scheduled — but ${res.conflicts.length} conflict(s) for ${tech}`, 'default');
      } else {
        toast.push('Appointment scheduled', 'success');
      }
      onClose();
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Could not schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title={`Schedule · ${job.title}`} onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit}>Book appointment</Button></>}>
      <div className="stack gap-4">
        {conflicts.length > 0 && <div className="banner banner-warning">Heads up: technician has {conflicts.length} overlapping appointment(s).</div>}
        <div className="row gap-3">
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Start"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          <Field label="Hours"><Input value={durationH} onChange={(e) => setDurationH(e.target.value)} style={{ width: 70 }} inputMode="decimal" /></Field>
        </div>
        <Field label="Assign technician" hint="Demo uses seeded user ids (e.g. usr_tech1)"><Input value={tech} onChange={(e) => setTech(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
